export interface WizardStep {
    /** Short imperative label for the step. */
    title: string;
    /** One-line expectation-setting description. */
    detail: string;
}

export const WIZARD_STEPS: WizardStep[] = [
    {
        title: "Add Address",
        detail: "Add an address to monitor for USDv positions."
    },
    {
        title: "Rewards Destination (Optional)",
        detail: "Tell us where you want to send your rewards."
    },
    {
        title: "Submit",
        detail: "Register your wallet for eligible programs."
    }
];

export const LAST_STEP = WIZARD_STEPS.length - 1;

export type VerifyStatus = "idle" | "verifying" | "verified" | "failed";

/** The discovery lifecycle inside step 0. `finding` is the in-flight discovery
 *  call (automatic after address entry, or awaited/retried by verification);
 *  `not-covered` is the hard "contact
 *  your issuer" stop; `already-registered` is the duplicate rejection. */
export type FindPositionStatus = "idle" | "finding" | "found" | "not-covered" | "already-registered" | "failed";

export type OwnershipSourceKind = "eoa" | "multisig-squads";

export type VerifyMessageTone = "muted" | "success" | "error";

/**
 * Rewards-destination treatments for the rewards step, swapped as the feature matures:
 *   - "locked" — MVP: rewards always go to the connected source wallet; the
 *     destination field is read-only and can't be changed.
 *   - "single" — user may set one custom destination (the original behavior).
 *   - "saved"  — optional pick from the Company Recipient's saved Rewards
 *     Destinations (searchable); a new address typed in can be saved and
 *     picked; nothing picked = the position source.
 *   - "multi"  — user may split rewards across multiple destinations (future).
 */
export type RewardsDestinationVariant = "locked" | "single" | "saved" | "multi";

/**
 * The active rewards-destination variant. Single source of truth — flip this
 * to change the treatment. The Company Recipient Console uses "saved" (the
 * Rewards Destinations page is always on there; retail never reads this).
 * "locked" and "single" are implemented and kept for the showcase; "multi"
 * is not built yet.
 */
export const REWARDS_DESTINATION_VARIANT: RewardsDestinationVariant = "saved";

/** The Rewards Destination step's index in `WIZARD_STEPS`. */
export const REWARDS_STEP = 1;

/**
 * Whether a step is skipped. In the "locked" variant (not used by the
 * console today) rewards go to the source and there is nothing to choose,
 * so the Rewards Destination step is never shown: Next goes from Add Address
 * straight to Submit, and the rail lists two steps. Indices stay the
 * `WIZARD_STEPS` ones; only display numbering closes the gap.
 */
export function isStepHidden(index: number, variant: RewardsDestinationVariant): boolean {
    return index === REWARDS_STEP && variant === "locked";
}

/** The next shown step after `active` (clamped to the last). */
export function nextShownStep(active: number, variant: RewardsDestinationVariant): number {
    let next = Math.min(active + 1, LAST_STEP);
    while (next < LAST_STEP && isStepHidden(next, variant)) next += 1;
    return next;
}

/** The previous shown step before `active` (clamped to the first). */
export function previousShownStep(active: number, variant: RewardsDestinationVariant): number {
    let prev = Math.max(active - 1, 0);
    while (prev > 0 && isStepHidden(prev, variant)) prev -= 1;
    return prev;
}

/** Resolved Squads multisig detail for a position source (null for EOAs). */
export type MultisigSummary = {
    address: string;
    vaultAddress?: string;
    threshold: number;
    memberCount: number;
    members?: { address: string; canVerify: boolean }[];
    signerStatus: "can-verify" | "cannot-verify" | "not-member" | "no-signer";
};

const STEP_COLLAPSE = {
    moveDuration: 0.55,
    ease: [0.16, 1, 0.3, 1] as const
};

/**
 * The step frame's one height for the whole flow, in px: the tallest step's
 * content at the two-column layout (445px measured with its action bar),
 * rounded up. The frame starts
 * here and stays here, so nothing grows or shrinks between steps. Narrower
 * layouts that wrap taller still raise it (`useLockedFrameHeight`).
 */
const STEP_FRAME_HEIGHT = 448;

export { STEP_COLLAPSE, STEP_FRAME_HEIGHT };

/* ── Per-step slices of the wizard state ────────────────────────────────
 * `useStepFlow` returns one slice per concern; each step component takes
 * only the slice(s) it renders, so the wiring names the dependency instead
 * of relaying forty flat props through every layer. */

/** The position-source address field + its classification context. Owned by
 *  the combined step 0 (Add Address). */
export interface SourceSlice {
    kind: OwnershipSourceKind | null;
    connectedWalletAddress: string | null;
    multisigSummary: MultisigSummary | null;
    address: string;
    addressTouched: boolean;
    onAddressChange: (next: string) => void;
    onAddressBlur: () => void;
    addressLabel: string;
    addressPlaceholder: string;
    addressInvalid: boolean;
    /** Why the pasted source can't be used, shown under the field; null when it can. */
    addressError: string | null;
    /** The source already has a position with this Company Recipient: the
     *  field takes its error state and an "Already Registered" badge sits
     *  beside the address-type badge. Known at the source step (any surface),
     *  with the discovery-time duplicate check as a backstop. */
    alreadyRegistered: boolean;
    /** Lookup-failed on the source address → show a "try again" retry link. */
    showRetry: boolean;
    onRetry: () => void;
    /** Replaces the account-type badge below the input:
     *  "Checking address..." during classification and the initial preview wait,
     *  and the micro-transfer status while that's mid-flight; null otherwise. */
    notice: string | null;
}

/** Display-ready preview of the discovered holding (found state only). */
export interface FoundPositionPreview {
    balanceLabel: string;
    zeroBalance: boolean;
    observedAmount?: string | null;
    totalPositions?: number;
    rows?: { key: string; label: string; surface: string; positionCount: number; balanceLabel: string }[];
    incomplete?: boolean;
}

/** Step 0 — the discovery outcome surfaced inside the combined verify step.
 *  Discovery runs automatically after address entry. This slice carries the
 *  in-flight/settled status,
 *  any hard-stop message (not-covered / already-registered / failed), and the
 *  Address balance preview available before verification, and the backend
 *  holdings preview available before verification. */
export interface FindSlice {
    status: FindPositionStatus;
    message: string | null;
    messageTone: VerifyMessageTone;
    /** Read-only USDv token balance; does not establish enrollment eligibility. */
    addressPreview: FoundPositionPreview | null;
    foundPosition: FoundPositionPreview | null;
}

/** Step 0 — the verify-ownership action (awaits or retries discovery). */
export interface VerifySlice {
    status: VerifyStatus;
    message: string | null;
    messageTone: VerifyMessageTone;
    actionLabel: string;
    actionDisabled: boolean;
    /** Label for the "verify directly" link, scoped to the client's issuer. */
    directLabel: string;
    isDirect: boolean;
    /** After the 3s "Verification Failed" hold: button reads "Retry" + icon, default state. */
    retryReady: boolean;
    onVerify: () => void;
    directDisabled: boolean;
    onVerifyDirect: () => void;
    /** The direct-verification note to the distributor. No field renders it
     *  (dropped from Add Address); the create API still accepts one. */
    manualReview?: {
        description: string;
        onDescriptionChange: (value: string) => void;
    };
}

/** Step 1 — the rewards destination. */
export interface RewardsSlice {
    /** Rewards destination address (blank → preserves the wallet destination). */
    destination: string;
    onDestinationChange: (value: string) => void;
    /** Default rewards destination: the wallet's current one, else its EOA
     *  address or Squads vault when known. */
    defaultAddress: string;
    /** The default's name: its saved destination label, else the holding
     *  address's name; empty for a kept destination that isn't saved. */
    defaultLabel?: string;
}

/**
 * Display-ready summary of the enrollment the last step will persist — an
 * address-level receipt, not a single-position form. Under the discovery model
 * the user enrolls an address to monitor; the receipt aggregates what was found
 * on it (position count + total USDv) and where rewards go. Null until discovery
 * and verification have settled (the step gates guarantee both before the last
 * step is reachable).
 */
export interface SubmitReview {
    /** Positions discovery surfaced on the address (1 in MVP; the discovered
     *  count once multi-position providers land). */
    totalPositions: number;
    /** Total USDv balance across the discovered positions, formatted ("$3.88"). */
    totalBalanceLabel: string;
    /** Rewards destination, the full address (the receipt fits it to its box). */
    rewardsDestinationAddress: string;
    /** Label of the saved Rewards Destination it matches, or null. */
    rewardsDestinationLabel: string | null;
}

/** Step 2 — the review receipt + create-position submit. */
/** The in-place re-verification offered on the Submit step once the captured
 *  proof has expired or may have been consumed: the same ceremony as step 1. */
export interface ReverifySlice {
    /** A submit rejection may have consumed the proof without expiring it. */
    reason?: "expired" | "submit-failed";
    /** idle (not started), verifying (ceremony running), failed. */
    status: "idle" | "verifying" | "failed";
    /** A failed re-verification has finished its 3s "Verification Failed"
     *  hold and the button is an enabled Retry. */
    retryReady: boolean;
    onVerifyAgain: () => void;
}

export interface SubmitSlice {
    claimRequired?: boolean;
    onConfirmClaim?: () => void;
    pendingReview?: { uuid: string; address: string; description: string; issuerName: string } | null;
    automaticEnrollment?: boolean;
    registeredPositionCount?: number | null;
    onSubmit: () => void;
    isSubmitting: boolean;
    error: string | null;
    submitted: boolean;
    review: SubmitReview | null;
    /** Requires a fresh proof after expiry or a potentially consumed submission. */
    reverify?: ReverifySlice | null;
    /** Clears the address and returns to step 1 (after an Already
     *  Registered rejection at submit). */
    onStartOver?: () => void;
}

/** Step navigation — index, gating, and the reveal timing. */
export interface NavSlice {
    /** Current step index; drives both the left rail and the right stack. */
    active: number;
    /** Jump to a step (the rail guards to completed steps only). */
    setActive: (index: number) => void;
    /** Whether the current step is satisfied (gates "Next"). */
    canAdvance: boolean;
    isLast: boolean;
    onNext: () => void;
    onBack: () => void;
    /** Seconds the newly-reached step waits before revealing (lets the prior
     *  step's collapse finish first). */
    revealDelay: number;
}
